import SessionHelper from '@stubhub/session-helper';

/**
 * This helper function was extracted from getNavConfig() function to reuse the logic and follow SOLID principals (:D)
 * @param {integer} storeId
 * @param {string}  locale
 * @param {Boolean} [userSignedIn=false]            Indicator of whether logged in or not.
 * @param {Boolean} [isSessionAuthEnabled=false]    Indicator of whether feature toggle for controlling Central Login is turned on or not.
 */
export const getLinkConfig = (storeId, locale, userSignedIn = false, isSessionAuthEnabled = false) => {
  let signedOutUserLinkConfig, linkConfig, linkConfigByLocale;
  const defaultLinkConfig = require(`../config/nav-links.config.json`);
  if (!userSignedIn && isSessionAuthEnabled) {
    signedOutUserLinkConfig = _getLinkConfigForSignedOutUser(defaultLinkConfig);
  }
  try {
    linkConfig = require(`../config/nav-links.config.${storeId}.json`);
    /* istanbul ignore else */
    if (locale) {
      linkConfigByLocale = require(`../config/nav-links.config.${storeId}.${locale}.json`);
    }
  } catch (e) {
    //ignore
  }
  return {
    ...defaultLinkConfig,
    ...signedOutUserLinkConfig,
    ...linkConfig,
    ...linkConfigByLocale
  };
};

/**
 * For now this function is being used in {SellFAQ} component - to get URL to FAQ (support.stubhub.XXX)
 * by store id and locale (language) from all of these ./config/**.json files using the {getLinkConfig()} function
 * @param storeId {number}
 * @param locale {string}
 * @return {*|string}
 */
export const getHelpLink = (storeId, locale) => {
  const linkConfig = getLinkConfig(storeId, locale);
  const helpURL = linkConfig['help-sell'] || linkConfig['help-center'] || linkConfig['help-topics'] || linkConfig['help'];
  return (helpURL && helpURL.href) || helpURL || 'https://support.stubhub.com';
};

/**
 * This method is to get the initiated data for Nav menu including the links. 
 * @param {number}  storeId                                An integer value representing different stores.
 * @param {string}  locale
 * @param {Boolean} [isSessionAuthEnabled=false]           Indicator of whether feature toggle for controlling Central Login is turned on or not.
 */
const getNavConfig = (storeId = 1, locale, isSessionAuthEnabled = false) => {
  let userSignedIn = SessionHelper.loginStatus();
  let excludeConfig = userSignedIn ? 'sign-in' : 'profile';
  let navConfig;
  let defaultNavConfig = require(`../config/nav.config.json`);
  try {
    navConfig = require(`../config/nav.config.${storeId}.json`);
  } catch (e) {
    navConfig = {};
  }
  navConfig = {...defaultNavConfig, ...navConfig};

  let menuConfig;
  let defaultMenuConfig = require(`../config/nav-menu.config.json`);
  try {
    menuConfig = require(`../config/nav-menu.config.${storeId}.json`);
  } catch (e) {
    menuConfig = {};
  }
  menuConfig = {...defaultMenuConfig, ...menuConfig};
  if (userSignedIn) {
    menuConfig.menu[0][0] = "my-account";
    menuConfig['menu-account'][0] = "profile";
  }

  const linkConfig = getLinkConfig(storeId, locale, userSignedIn, isSessionAuthEnabled);
  // get href / noFormat props for menu item config
  // cannot reach instance without item given
  /* istanbul ignore next */
  const deconstructMenuItemHref = (item = {}) => {
    return typeof item === 'string' ? {href: item} : item;
  };

  const getMenuItems = (id) => {
    // Do not have menuConfigs with type link, please remove ignore if added in future
    /* istanbul ignore next */
    return menuConfig[id] ? menuConfig[id].map((item) => {
      if (typeof item === 'string') {
        return {
          id: item,
          type: 'link',
          ...deconstructMenuItemHref(linkConfig[item])
        };
      } else if (item.type === 'link') {
        return {
          id: item.id,
          type: 'link',
          ...deconstructMenuItemHref(linkConfig[item.id])
        };
      } else if (item.type === 'menu') {
        return {
          id: item.id,
          type: 'menu',
          items: getMenuItems(item.id)
        };
      } else if (item.type === 'hover') {
        return {
          id: item.id,
          type: 'menu',
          items: getMenuItems(item.id),
          ...deconstructMenuItemHref(linkConfig[item.id])
        };
      } else if (item.type === 'section') {
        return {
          id: item.id,
          type: 'section',
          items: getMenuItems(item.id)
        };
      }

      return undefined;
    }) : [];
  };

  // if guest session is established, validate that it should remain open.
  // guest session requirements that guest sessions are only allowed on checkout and myaccount
  /* istanbul ignore next */
  if (SessionHelper.loginType() === 'guest') {
    userSignedIn = SessionHelper.validateGuest({});
  }

  let data = {};
  /*
   * Filter the navigation config to remove the sign-in menu when authenticated user cookie set
   * TODO: Remove this once login is implemented in Bullet
  **/
  let navConfigPath = navConfig.actions;
  navConfig.actions = navConfigPath.filter((config) => config.id !== excludeConfig);
  Object.keys(navConfig).forEach((groupKey) => {
    data[groupKey] = navConfig[groupKey].map((item) => {
      /* istanbul ignore else */
      if (item.type === 'link') {
        return {
          id: item.id,
          type: 'link',
          ...deconstructMenuItemHref(linkConfig[item.id])
        };
      } else if (item.type === 'menu') {
        return {
          id: item.id,
          type: 'menu',
          items: getMenuItems(item.id)
        };
      } else if (item.type === 'hover') {
        return {
          id: item.id,
          type: 'menu',
          items: getMenuItems(item.id),
          ...deconstructMenuItemHref(linkConfig[item.id])
        };
      } else {
        return undefined;
      }
    });
  });
  return data;
};

const _getLinkConfigForSignedOutUser = (defaultLinkConfig) => {
  const {origin, originHref} = _getOriginAndHrefValues();
  if (origin && originHref) {
    return {
      ...defaultLinkConfig,
      "sign-in": origin + '/login?redirect=' + encodeURIComponent(originHref),
      "my-tickets": origin + '/login?redirect=' + encodeURIComponent(origin + '/my/orders')
    };
  }

  return {
    ...defaultLinkConfig
  };
};

const _getOriginAndHrefValues = () => {
  if (typeof window !== 'undefined') {
    const origin = window.location.origin || (window.location.protocol +
      "//" + window.location.hostname +
      (window.location.port ? ':' + window.location.port : '')); 
    const originHref = window.location.href;
    return {origin, originHref};
  } 
  return {origin: null, originHref: null};
};

export default getNavConfig;
